/**
 * Debug-driven method-tab pin / close.
 * tabs[0] is the transient slot; tabs[1+] are pinned.
 * userPinned is set only by the pin icon; debug auto-pins omit it.
 */

export function debugTabTransition({ tabs, leavingId, enteringId, leavingStillOnStack }) {
    if (!leavingId || !enteringId || leavingId === enteringId)
        return { action: 'none' };

    const list = tabs || [];
    const leavingTabIndex = list.findIndex((t) => t && t.value == leavingId);
    const leavingTab = leavingTabIndex >= 0 ? list[leavingTabIndex] : null;

    if (leavingStillOnStack) {
        if (leavingTabIndex === 0 && leavingTab?.text && leavingTab?.value)
            return { action: 'pin-leaving' };
        return { action: 'none' };
    }

    if (!leavingTab || leavingTab.userPinned)
        return { action: 'none' };
    return { action: 'close-leaving', closeIndex: leavingTabIndex };
}

export function applyDebugTabTransition(tabs, decision) {
    const next = (tabs || []).map((t) => ({ ...t }));
    if (!decision || decision.action === 'none')
        return next;

    if (decision.action === 'pin-leaving') {
        if (!next[0]?.text || !next[0]?.value)
            return next;
        const pinned = {
            text: next[0].text,
            value: next[0].value,
            type: next[0].type,
            userPinned: false,
        };
        next[0] = { text: '', value: '', type: '', id: '' };
        if (next.length > 1)
            next.splice(1, 0, pinned);
        else
            next.push(pinned);
        return next;
    }

    if (decision.action === 'close-leaving') {
        const i = decision.closeIndex;
        if (i === 0 && next[0]) {
            next[0] = { text: '', value: '', type: '', id: '' };
        }
        else if (i > 0) {
            next.splice(i, 1);
        }
        return next;
    }

    return next;
}

function emptyTransientSlot() {
    return { text: '', value: '', type: '', id: '' };
}

export function pinTransientTabAsUser(tabs) {
    const next = (tabs || []).map((t) => ({ ...t }));
    if (!next[0]?.text || !next[0]?.value)
        return next;
    const pinned = {
        text: next[0].text,
        value: next[0].value,
        type: next[0].type,
        userPinned: true,
    };
    next[0] = emptyTransientSlot();
    if (next.length > 1)
        next.splice(1, 0, pinned);
    else
        next.push(pinned);
    return next;
}

/** Move a pinned tab (index 1+) back to the transient slot. Canvas stays on that method. */
export function unpinPinnedTabToTransient(tabs, id) {
    const next = (tabs || []).map((t) => ({ ...t }));
    if (!id)
        return next;
    const i = next.findIndex((t, idx) => idx > 0 && t && t.value == id);
    if (i < 1)
        return next;
    const tab = next[i];
    next[0] = {
        text: tab.text,
        value: tab.value,
        type: tab.type,
        id: tab.id || '',
    };
    next.splice(i, 1);
    return next;
}

/** Remove a pinned tab only. Never clears tabs[0] (the current-method slot). */
export function closePinnedTab(tabs, id) {
    const next = (tabs || []).map((t) => ({ ...t }));
    if (!id)
        return next;
    const i = next.findIndex((t, idx) => idx > 0 && t && t.value == id);
    if (i < 1)
        return next;
    next.splice(i, 1);
    return next;
}

/** Selecting a different pinned tab dismisses the leftover transient method. */
export function clearTransientIfSelectingPinned(tabs, id) {
    const next = (tabs || []).map((t) => ({ ...t }));
    if (!id || !next[0]?.value || next[0].value == id)
        return next;
    const pinned = next.find((t, idx) => idx > 0 && t && t.value == id);
    if (!pinned)
        return next;
    next[0] = emptyTransientSlot();
    return next;
}
